/**
 * Voice Warmup - Microphone permission and voice drawing warm-up
 * Ages 4-6, Accessibility-focused
 */

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { VoiceEngine } from './VoiceEngine';
import { VoiceMark } from './types';

interface VoiceWarmupProps {
  language: 'en' | 'es';
  onComplete: (data: { permissionGranted: boolean }) => void;
  onSkip: () => void;
}

const TEXT = {
  en: {
    title: 'Voice Warm-Up!',
    intro: 'Let\'s warm up your voice by making sounds!',
    instructions: 'Make sounds to draw colorful lines',
    quiet: 'Quiet sounds = thin lines',
    loud: 'Loud sounds = thick lines',
    requestMic: 'Use Microphone',
    skip: 'Skip Warm-Up',
    tryIt: 'Try making sounds now!',
    louder: 'Try louder!',
    softer: 'Try softer!',
    great: 'Great job!',
    continue: 'Continue',
    micError: 'We need your microphone to draw with sound. You can skip this step.',
    makeSound: 'Make a sound!'
  },
  es: {
    title: '¡Calentamiento de Voz!',
    intro: '¡Calentemos tu voz haciendo sonidos!',
    instructions: 'Haz sonidos para dibujar líneas coloridas',
    quiet: 'Sonidos suaves = líneas delgadas',
    loud: 'Sonidos fuertes = líneas gruesas',
    requestMic: 'Usar Micrófono',
    skip: 'Saltar Calentamiento',
    tryIt: '¡Prueba hacer sonidos ahora!',
    louder: '¡Prueba más fuerte!',
    softer: '¡Prueba más suave!',
    great: '¡Muy bien!',
    continue: 'Continuar',
    micError: 'Necesitamos tu micrófono para dibujar con sonido. Puedes saltar este paso.',
    makeSound: '¡Haz un sonido!'
  }
};

export default function VoiceWarmup({ language, onComplete, onSkip }: VoiceWarmupProps) {
  const [phase, setPhase] = useState<'intro' | 'requesting' | 'active' | 'error'>('intro');
  const [marks, setMarks] = useState<VoiceMark[]>([]);
  const [currentColor, setCurrentColor] = useState('#FF6B9D');
  const [encouragement, setEncouragement] = useState('');

  const voiceEngine = useRef<VoiceEngine>(new VoiceEngine());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const lastPositionRef = useRef<{ x: number; y: number } | null>(null);
  const continuousSoundTimeRef = useRef<number>(0);
  const pitchHistoryRef = useRef<number[]>([]);

  const t = TEXT[language];

  // Color palette for continuous sound
  const colors = ['#FF6B9D', '#4ECDC4', '#95E1D3', '#FFD93D', '#6BCF7F', '#A855F7'];

  // Request microphone access
  const handleRequestMicrophone = async () => {
    setPhase('requesting');

    const granted = await voiceEngine.current.requestMicrophoneAccess();

    if (granted) {
      setPhase('active');
      startDrawing();
    } else {
      setPhase('error');
    }
  };

  // Start voice-reactive drawing
  const startDrawing = () => {
    const draw = () => {
      const properties = voiceEngine.current.getVoiceProperties();

      if (properties.isActive && canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Increment continuous sound time
        continuousSoundTimeRef.current += 1;

        // Change color after continuous sound (every 30 frames = ~0.5 seconds)
        if (continuousSoundTimeRef.current % 30 === 0) {
          const colorIndex = Math.floor(continuousSoundTimeRef.current / 30) % colors.length;
          setCurrentColor(colors[colorIndex]);
        }

        // Calculate position based on pitch (0-1 → canvas height)
        const y = canvas.height * (1 - properties.pitch); // Invert so high pitch = top

        // Calculate thickness based on volume with exponential scaling for MORE dramatic effect
        // Using power of 1.8 makes quiet sounds thinner and loud sounds MUCH thicker
        // Range: 1px (quiet) to 60px (very loud)
        const thickness = 1 + (Math.pow(properties.volume, 1.8) * 59);

        // Store pitch history for curve calculation (keep last 3 values)
        pitchHistoryRef.current.push(properties.pitch);
        if (pitchHistoryRef.current.length > 3) {
          pitchHistoryRef.current.shift();
        }

        // Current x position (moving left to right)
        const x = lastPositionRef.current
          ? lastPositionRef.current.x + 5
          : 0;

        // Wrap around when reaching the end
        const actualX = x % canvas.width;

        // Clear column if wrapping around
        if (actualX < (lastPositionRef.current?.x || 0)) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          pitchHistoryRef.current = []; // Reset pitch history on wrap
        }

        // Draw CURVED line from last position to current (pitch-based curves!)
        if (lastPositionRef.current) {
          ctx.strokeStyle = currentColor;
          ctx.lineWidth = thickness;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          ctx.beginPath();
          ctx.moveTo(lastPositionRef.current.x, lastPositionRef.current.y);

          // Calculate pitch change rate for curve intensity
          const pitchChange = pitchHistoryRef.current.length >= 2
            ? Math.abs(pitchHistoryRef.current[pitchHistoryRef.current.length - 1] -
                       pitchHistoryRef.current[pitchHistoryRef.current.length - 2])
            : 0;

          // Create curved line using quadratic curve
          // Control point is influenced by pitch change - more change = more curve
          const midX = (lastPositionRef.current.x + actualX) / 2;
          const midY = (lastPositionRef.current.y + y) / 2;

          // Curve offset based on pitch change (0-100px offset)
          // Higher pitch variation creates more dramatic curves
          const curveIntensity = pitchChange * 200;
          const pitchDirection = properties.pitch > 0.5 ? 1 : -1; // Curve direction based on pitch

          const controlX = midX;
          const controlY = midY + (curveIntensity * pitchDirection);

          // Use quadratic curve for smooth, pitch-responsive drawing
          ctx.quadraticCurveTo(controlX, controlY, actualX, y);
          ctx.stroke();
        }

        lastPositionRef.current = { x: actualX, y };

        // Show encouragement based on volume
        if (properties.volume > 0.7) {
          setEncouragement(t.louder);
        } else if (properties.volume < 0.2 && properties.volume > 0.05) {
          setEncouragement(t.softer);
        } else if (properties.volume > 0.3) {
          setEncouragement(t.great);
        }
      } else {
        // Reset continuous sound counter when silent
        continuousSoundTimeRef.current = 0;
        pitchHistoryRef.current = []; // Reset pitch history when silent
        setEncouragement(t.makeSound);
      }

      animationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      voiceEngine.current.stop();
    };
  }, []);

  // INTRO PHASE
  if (phase === 'intro') {
    return (
      <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-8 text-center"
        >
          <div className="text-8xl mb-6">🎤</div>

          <h2
            className="text-5xl md:text-6xl mb-4"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontWeight: 300,
              color: '#2a6d5b',
              letterSpacing: '-0.015em',
            }}
          >
            {t.title}
          </h2>

          <p
            className="text-2xl md:text-3xl mb-6"
            style={{ fontFamily: "'Inter', system-ui, sans-serif", color: '#355c46' }}
          >
            {t.intro}
          </p>

          <div className="rounded-2xl p-6 mb-8" style={{ background: '#f4efde' }}>
            <p
              className="mb-3"
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontStyle: 'italic',
                fontSize: 24,
                color: '#2a6d5b',
              }}
            >
              {t.instructions}
            </p>
            <div className="space-y-2" style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 18, color: '#5f6e66' }}>
              <p>{t.quiet}</p>
              <p>{t.loud}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleRequestMicrophone}
              style={{
                background: '#42a57a',
                color: '#ffffff',
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 20,
                fontWeight: 600,
                padding: '14px 28px',
                borderRadius: 999,
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(66,165,122,0.25)',
              }}
            >
              {t.requestMic}
            </button>

            <button
              onClick={onSkip}
              style={{
                background: 'transparent',
                color: '#42a57a',
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 20,
                fontWeight: 600,
                padding: '14px 28px',
                borderRadius: 999,
                border: '1.5px solid #42a57a',
                cursor: 'pointer',
              }}
            >
              {t.skip}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // REQUESTING PHASE
  if (phase === 'requesting') {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          className="text-8xl"
        >
          🎤
        </motion.div>
      </div>
    );
  }

  // ERROR PHASE
  if (phase === 'error') {
    return (
      <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center p-6">
        <div className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
          <div className="text-8xl mb-6">😔</div>

          <p
            className="mb-6"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontStyle: 'italic',
              fontSize: 26,
              color: '#2a6d5b',
            }}
          >
            {t.micError}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleRequestMicrophone}
              style={{
                background: '#42a57a',
                color: '#ffffff',
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 20,
                fontWeight: 600,
                padding: '14px 28px',
                borderRadius: 999,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {t.requestMic}
            </button>

            <button
              onClick={onSkip}
              style={{
                background: 'transparent',
                color: '#42a57a',
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 20,
                fontWeight: 600,
                padding: '14px 28px',
                borderRadius: 999,
                border: '1.5px solid #42a57a',
                cursor: 'pointer',
              }}
            >
              {t.skip}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ACTIVE PHASE
  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center p-6">
      <div className="max-w-4xl w-full">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-6"
        >
          <p
            className="mb-2"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontWeight: 300,
              fontSize: 'clamp(36px, 5vw, 56px)',
              color: '#2a6d5b',
              letterSpacing: '-0.015em',
            }}
          >
            {t.tryIt}
          </p>

          {encouragement && (
            <motion.p
              key={encouragement}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontStyle: 'italic',
                fontSize: 28,
                fontWeight: 500,
                color: '#42a57a',
              }}
            >
              {encouragement}
            </motion.p>
          )}
        </motion.div>

        {/* Voice Drawing Canvas */}
        <div className="bg-white rounded-3xl shadow-2xl p-4 mb-6">
          <canvas
            ref={canvasRef}
            width={800}
            height={400}
            className="w-full h-auto rounded-2xl bg-gray-50"
          />
        </div>

        {/* Continue Button */}
        <div className="text-center">
          <button
            onClick={() => {
              voiceEngine.current.stop();
              onComplete({ permissionGranted: true });
            }}
            style={{
              background: '#42a57a',
              color: '#ffffff',
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 22,
              fontWeight: 600,
              padding: '16px 36px',
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(66,165,122,0.25)',
            }}
          >
            {t.continue} →
          </button>
        </div>
      </div>
    </div>
  );
}
